import { useCallback } from 'react';
import _ from 'lodash';
import { gql } from '@apollo/client';
import { useEffectOnce } from 'react-use';

import MetricsHeader from '../components/layout/MetricsHeader';
import { CropDisplayFragment } from '../queries';
import useFarmOrSectionGroupData from '../hooks/useFarmOrSectionGroupData';
import { colorForCrop } from '../utils/colors';
import { useSubsidySwitchContext } from '../contexts/SubsidySwitchContext';
import QuoteSummary from '../components/economics/QuoteSummary';
import { captureEvent } from '../utils/analytics';
import { useDataTransforms } from '../utils/hooks';
import { safeDivide } from '../utils/utils';
import { useUnits } from '../contexts/UnitContext';
import { LONG_AREA_UNITS } from '../constants/units';

const FRAGMENT_NAME = 'QuoteSummaryFragment';
const QUERY_NAME = 'QuoteSummaryQuery';
const QuoteSummaryFragment = gql`
  ${CropDisplayFragment}

  fragment ${FRAGMENT_NAME} on SectionsAggregate {
    cropAggregates(
      applyMargin: true
      applySubsidy: $applySubsidy
    ) {
      crop {
        ...CropDisplayFields
      }

      economicSummary(
        applyMargin: true
        applySubsidy: $applySubsidy
      ) {
        installationOpex(assignment: "developer", excludeReplants: true)
        initialGeneticsCost # (excludes replants)
      }

      ecologicalSummary {
        treeCount
        acreage
      }
    }
  }
`;

const QuoteSummaryView = () => {
  useEffectOnce(() => {
    captureEvent('quote-summary-pageview');
  });
  const { shouldShowSubsidies } = useSubsidySwitchContext();

  const { transformToUnit: transformArea } = useUnits(LONG_AREA_UNITS);

  // Aggregate the flat fetched data by crop
  const aggregateFetchedData = useCallback(
    (data = {}) => {
      const { cropAggregates = [] } = data;

      const byCrop = cropAggregates.map(
        ({ crop, economicSummary, ecologicalSummary }) => {
          const { id: cropId } = crop;
          const {
            installationOpex,
            initialGeneticsCost: geneticStockCost,
          } = economicSummary;
          const { treeCount, acreage } = ecologicalSummary;

          const installationCost = _.sum(installationOpex);
          const totalCost = installationCost + geneticStockCost;

          const area = transformArea(acreage);

          return {
            id: cropId, // unique key per row
            cropName: crop.name,
            installationCost,
            geneticStockCost,
            totalCost,
            color: colorForCrop(crop),
            stems: treeCount,
            area,
            costPerStem: safeDivide(totalCost, treeCount),
            costPerArea: safeDivide(totalCost, area),
          };
        },
      );

      return _.orderBy(byCrop, 'totalCost', 'desc');
    },
    [transformArea],
  );

  const { msg, data } = useFarmOrSectionGroupData(
    QuoteSummaryFragment,
    QUERY_NAME,
    FRAGMENT_NAME,
    [
      {
        name: 'applySubsidy',
        typeString: 'Boolean',
        value: shouldShowSubsidies,
      },
    ],
  );

  const [dataByCrop = []] = useDataTransforms(data, [aggregateFetchedData]);

  return (
    <>
      <MetricsHeader>Quote Summary</MetricsHeader>
      <QuoteSummary msg={msg} dataByCrop={dataByCrop} />
    </>
  );
};

export default QuoteSummaryView;
